/**
 * Выбор языка: глобус в углу и список под ним.
 *
 * Раньше языки лежали строкой прямо в форме входа и занимали место у четырёх
 * штук. В углу они не мешают, а найти их там привычно.
 */

import { useEffect, useRef, useState } from 'react'

import { LANGUAGES, LANG_LABEL, dict, rememberLang, type Lang } from './i18n'

type Props = {
  lang: Lang
  onLang: (lang: Lang) => void
}

export function LangPicker({ lang, onLang }: Props) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const t = dict(lang)

  // Закрываем по нажатию вне списка и по Escape — иначе он останется висеть.
  useEffect(() => {
    if (!open) return
    function onPointer(event: PointerEvent) {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="lang" ref={root}>
      <button
        type="button"
        className="lang-globe"
        aria-label={t.language}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <GlobeIcon />
      </button>

      {open && (
        <ul className="lang-menu" role="listbox" aria-label={t.language}>
          {LANGUAGES.map((code) => (
            <li key={code}>
              <button
                type="button"
                role="option"
                aria-selected={code === lang}
                onClick={() => {
                  onLang(code)
                  rememberLang(code)
                  setOpen(false)
                }}
              >
                {LANG_LABEL[code]}
                {code === lang && <span className="tick">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function GlobeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3Z" />
    </svg>
  )
}
